import React, { useEffect, useRef, useState } from 'react'
import * as Icons from 'react-feather'
import DataTable from 'react-data-table-component'
import { Link } from 'react-router-dom'
import axios from 'axios'
import SKAlerts, { CreateAlert } from '../../shared/common-alerts/SKAlerts'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'
import StatusGridDropdown from '../../shared/common-dropdown/StatusGridDropdown'
import { getStorageValues } from '../../shared/CommonFunctions'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { logAuditEvent } from '../../utils/auditLogger'
import HandoverRegisterModal from './HandoverRegisterModal'
import HandoverRegisterExpandedRow from './HandoverRegisterExpandedRow'
import HandoverRegisterModel from './models/HandoverRegisterModel'

function HandoverRegisterGrid(props) {
  const [actionType, setActionType] = useState()
  const [modalIsOpen, setIsOpen] = useState(false)
  const [handoverModel, setHandoverModel] = useState(new HandoverRegisterModel())
  const [filterText, setFilterText] = useState('')
  const [alert, setAlert] = useState(null)
  const [editableCell, setEditableCell] = useState({ rowId: null, column: null, salesId: null })
  const [handoverRows, setHandoverRows] = useState([])
  const [expandedRowId, setExpandedRowId] = useState(null)
  const expandedRef = useRef(null)
  const moduleName = props.moduleName || 'Handover Register'

  useEffect(() => {
    const eligible = Array.isArray(props.EligibleData) ? props.EligibleData : []
    const completed = Array.isArray(props.CompletedData) ? props.CompletedData : []
    setHandoverRows([...eligible, ...completed])
  }, [props.EligibleData, props.CompletedData])

  const ensureHandoverRowWithStatus = async ({ row, statusId, statusLabel }) => {
    if (!row?.SalesRegisterId) return null
    if (row?.HandoverRegisterId) return row.HandoverRegisterId
    try {
      const response = await axios.post(getApiBaseUrl() + '/handoverregister/ensure', {
        SalesRegisterId: row.SalesRegisterId,
        StatusId: statusId || null,
        Remark: null,
        UserId: getStorageValues('userId')
      })
      const id = response?.data?.data?.HandoverRegisterId || response?.data?.HandoverRegisterId || null
      if (!id) return null

      setHandoverRows((prev) =>
        (prev || []).map((r) => (
          r?.SalesRegisterId === row.SalesRegisterId
            ? {
              ...r,
              HandoverRegisterId: id,
              StatusId: statusId ?? r?.StatusId ?? null,
              Status: statusLabel || r?.Status || 'Pending'
            }
            : r
        ))
      )
      return id
    } catch (error) {
      console.log(error)
      CreateAlert('Unable to start handover for selected sale', 'Error', setAlert)
      return null
    }
  }

  const rows = (handoverRows || []).filter((item) => (
    (item.SalesNumber && item.SalesNumber.toLowerCase().includes(filterText.toLowerCase())) ||
    (item.CustomerName && item.CustomerName.toLowerCase().includes(filterText.toLowerCase())) ||
    (item.MobileNumber && item.MobileNumber.toLowerCase().includes(filterText.toLowerCase())) ||
    (item.PaymentMode && item.PaymentMode.toLowerCase().includes(filterText.toLowerCase())) ||
    (item.Status && item.Status.toLowerCase().includes(filterText.toLowerCase()))
  ))

  const openActionType = (row, name) => {
    logAuditEvent(moduleName, 'View/Email Button Click', JSON.stringify({
      Id: row?.HandoverRegisterId,
      Name: row?.CustomerName,
      Action: name
    }))
    setHandoverModel(row)
    setIsOpen(true)
    setActionType(name)
  }

  const closeModal = () => {
    setIsOpen(false)
    props.RefreshEligible?.()
  }

  const updateStatus = async (option, row) => {
    if (!option || !row) return

    try {
      let handoverRegisterId = row?.HandoverRegisterId
      if (!handoverRegisterId) {
        handoverRegisterId = await ensureHandoverRowWithStatus({ row, statusId: option.value, statusLabel: option.label })
        if (!handoverRegisterId) return

        CreateAlert('Status updated successfully!!', 'Success', setAlert)
        logAuditEvent(moduleName, 'Status Insert (ensure)', JSON.stringify({
          SalesRegisterId: row?.SalesRegisterId,
          Id: handoverRegisterId,
          StatusId: option.value,
          Status: option.label
        }))
        props.RefreshEligible?.()
        return
      }

      const response = await axios.post(getApiBaseUrl() + '/handoverregister/updatestatus', {
        HandoverRegisterId: handoverRegisterId,
        StatusId: option.value,
        UserId: getStorageValues('userId')
      })
      if (response.status === 200) {
        CreateAlert('Status updated successfully!!', 'Success', setAlert)
        logAuditEvent(moduleName, 'Status Update', JSON.stringify({
          Id: handoverRegisterId,
          StatusId: option.value,
          Status: option.label
        }))
        props.RefreshEligible?.()
      }
    } catch (error) {
      console.log(error)
      const message = error?.response?.data?.message || 'Unable to update status'
      CreateAlert(message, 'Error', setAlert)
    }
  }

  const columns = [
    {
      name: <div className="titlename"><span>Customer Sales#</span></div>,
      selector: row => row.SalesNumber,
      cell: (row) => (
        <span
          title={`${row.SalesNumber} (${row.CustomerName || '-'})`}
          style={{
            fontWeight: props.HighlightSalesRegisterId && row?.SalesRegisterId === props.HighlightSalesRegisterId ? 700 : 400
          }}
        >
          {row.SalesNumber} ({row.CustomerName || '-'})
        </span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Payment Mode</span></div>,
      selector: row => row.PaymentMode,
      cell: (row) => (
        <span title={row.PaymentMode}>{row.PaymentMode || '-'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Customer Mobile</span></div>,
      selector: row => row.MobileNumber,
      cell: (row) => (
        <span title={row.MobileNumber}>{row.MobileNumber || '-'}</span>
      ),
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: (
        <div className="titlename">
          <span>Status</span>
        </div>
      ),
      selector: row => row.Status,
      cell: (row) => {
        const statusLower = String(row?.Status || '').trim().toLowerCase()
        const isCompleted = statusLower === 'complete' || statusLower === 'completed'
        const isEditing = (
          editableCell.column === 'Status'
          && (
            (row?.HandoverRegisterId && editableCell.rowId === row.HandoverRegisterId)
            || (!row?.HandoverRegisterId && editableCell.salesId && editableCell.salesId === row?.SalesRegisterId)
          )
        )
        if (isCompleted) {
          return (
            <div
              style={{
                opacity: 0.9,
                cursor: 'not-allowed',
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Completed entry cannot be modified"
            >
              <div className="cell-content">{row.Status}</div>
            </div>
          )
        }

        return isEditing ? (
          <StatusGridDropdown
            key={`status-${row.HandoverRegisterId || row?.SalesRegisterId || 'new'}`}
            autoFocus={true}
            showLabel={false}
            selectedValue={row.StatusId}
            onChangeDropdown={(e) => {
              updateStatus(e, row)
              setEditableCell({ rowId: null, column: null, salesId: null })
            }}
            onBlur={() => setEditableCell({ rowId: null, column: null, salesId: null })}
            openMenuOnFocus={true}
            openMenuOnClick={true}
          />
        ) : (
          <div
            className="editable-cell-with-icon"
            onClick={() => {
              // For eligible rows without HandoverRegisterId, we still allow opening dropdown UI.
              // The insert happens only after user selects a status.
              setEditableCell({ rowId: row.HandoverRegisterId || 0, column: 'Status', salesId: row?.SalesRegisterId })
            }}
            style={{ cursor: 'pointer', height: '100%', width: '100%', display: 'flex', alignItems: 'center' }}
          >
            <div className="cell-content">{row.Status}</div>
            <span className="edit-icon">
              <Icons.Edit size={12} color="#1261a0" />
            </span>
          </div>
        )
      },
      sortable: true,
      sortIcon: <SortArrow />
    }
  ]

  if (props.UserPermissions.ViewModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          <Link
            className="me-2 p-2"
            to=""
            onClick={() => {
              openActionType(row, 'View')
            }}
          >
            <Icons.Eye data-feather="eye" className="action-eye" />
          </Link>
          <Link
            className="me-2 p-2"
            to=""
            onClick={() => {
              openActionType(row, 'Email')
            }}
            title="Send Email/WhatsApp"
          >
            <Icons.Mail data-feather="mail" size="14" className="feather-mail" />
          </Link>
        </div>
      ),
      center: 'true',
      width: '150px'
    })
  }

  const handleRowExpandToggled = (expanded, row) => {
    if (!expanded) {
      setExpandedRowId((prev) => (prev === row?.HandoverRegisterId ? null : prev))
      return
    }

    if (!row?.HandoverRegisterId) {
      CreateAlert('Set status first to start handover and upload documents.', 'Error', setAlert)
      return
    }
    setExpandedRowId(row.HandoverRegisterId)
  }

  const isRowExpanded = (row) => row.HandoverRegisterId === expandedRowId

  const conditionalRowStyles = [
    {
      when: (row) => {
        const st = String(row?.Status || '').trim().toLowerCase()
        return st === 'complete' || st === 'completed'
      },
      style: {
        backgroundColor: '#e7f1ff', // light blue for completed rows
      },
    },
  ]

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div
              className="search-set"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                width: '100%'
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: 12 }}>
                <div className="search-input">
                  <button type="button" className="btn btn-searchset">
                    <Icons.Search className="feather-search" />
                  </button>
                  <input
                    id="search"
                    type="text"
                    className="form-control form-control-sm custom-search-input"
                    placeholder="Search"
                    aria-label="Search Input"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center" style={{ gap: 8, whiteSpace: 'nowrap' }}>
                <input
                  id="showCompletedEntries"
                  type="checkbox"
                  checked={!!props.ShowCompleted}
                  onChange={(e) => props.OnToggleShowCompleted?.(e.target.checked)}
                />
                <label htmlFor="showCompletedEntries" style={{ marginBottom: 0, fontSize: 13, cursor: 'pointer' }}>
                  Show completed entries
                </label>
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            conditionalRowStyles={conditionalRowStyles}
            pagination
            defaultSortField="SalesNumber"
            sortIcon={<SortArrow />}
            persistTableHead={true}
            expandableRows
            onRowExpandToggled={handleRowExpandToggled}
            expandableRowExpanded={isRowExpanded}
            expandableRowsComponent={HandoverRegisterExpandedRow}
            expandableRowsComponentProps={{ ref: expandedRef }}
          />
        </div>
      </div>
      <HandoverRegisterModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={handoverModel}
        moduleName={moduleName}
      />
    </>
  )
}

export default HandoverRegisterGrid
